// Typed query-key registry. Centralising keys keeps cache reads, writes and
// invalidations in lockstep across the data layer and avoids stringly-typed
// drift. List keys are `as const` tuples; entity keys are builder functions.
export const queryKeys = {
  transactions: ['transactions'] as const,
  // Admin Manage screen: whole population INCLUDING soft-deleted rows. A child of
  // the `transactions` prefix, so invalidateRegister refreshes it for free.
  manageTransactions: ['transactions', 'manage'] as const,
  findings: ['findings'] as const,
  cesRows: ['cesRows'] as const,
  cesRowsByRef: ['cesRows', 'byRef'] as const,
  workingPapers: ['workingPapers'] as const,
  allocations: ['allocations'] as const,
  users: ['users'] as const,
  accessGrants: ['accessGrants'] as const,
  accessAuditLog: ['accessAuditLog'] as const,
  nameDirectory: ['nameDirectory'] as const,
  // In-app notifications (the caller's own) + QA notes on working papers (4a).
  notifications: ['notifications'] as const,
  qaNotes: ['qaNotes'] as const,
  // Cross-transaction AI review summary (AI Assurance report). Not part of the
  // register surface, so invalidateRegister leaves it alone.
  aiReviewLatestAll: ['aiReview', 'latestAll'] as const,
  // Reviewer-scoped register (one server round-trip shared by the transactions /
  // working-papers / findings read hooks). The prefix form exists so mutations
  // can invalidate EVERY reviewer's scoped cache without knowing emails.
  scopedRegister: ['register', 'mine'] as const,
  scopedRegisterFor: (email: string) => ['register', 'mine', email] as const,
  transactionByRef: (ref: string) => ['transactions', 'byRef', ref] as const,
  workingPaperByTx: (ref: string) => ['workingPapers', 'byTx', ref] as const,
  accessGrantByEmail: (email: string) => ['accessGrants', 'byEmail', email] as const,
  // Audit trail — all under one `auditTrail` prefix so a single invalidation line
  // refreshes the admin-direct and reviewer-scoped variants together. The
  // per-transaction trail is keyed by ref (admin) / email+ref (scoped), the snapshot
  // detail by its id set, and the global feed by its date window.
  auditTrailByRef: (ref: string) => ['auditTrail', 'byRef', ref] as const,
  scopedAuditTrailByRef: (email: string, ref: string) =>
    ['auditTrail', 'byRef', 'mine', email, ref] as const,
  evaluationSnapshot: (ids: readonly string[]) => ['auditTrail', 'snapshot', ids.join(',')] as const,
  auditFeed: (from: string, to: string) => ['auditTrail', 'feed', from, to] as const,
} as const;

/** The minimal QueryClient surface the invalidation helper needs (mock-friendly). */
interface Invalidator {
  invalidateQueries(filters: { queryKey: readonly string[] }): Promise<void> | void;
}

/**
 * Invalidate the WHOLE register read surface after any register-touching write:
 * transactions, working papers, findings, CES rows — and the reviewer-scoped
 * `['register','mine',…]` caches (matched by prefix). The narrow byRef / byTx
 * entries are covered through prefix matching on the broad keys. This is the
 * single freshness chokepoint: every mutation hook calls it, so an edit made in
 * one view (transaction vs working paper) is visible in the other without a
 * hard refresh, for admins AND scoped reviewers.
 */
export function invalidateRegister(qc: Invalidator): void {
  void qc.invalidateQueries({ queryKey: queryKeys.transactions });
  void qc.invalidateQueries({ queryKey: queryKeys.workingPapers });
  void qc.invalidateQueries({ queryKey: queryKeys.findings });
  void qc.invalidateQueries({ queryKey: queryKeys.cesRows });
  void qc.invalidateQueries({ queryKey: queryKeys.scopedRegister });
}
